import createModule from "../createModule"
import view from "./view"
import {
  getSortedContributors,
  getAvailableRollups,
  getContributorsSummaryText,
  getExcelData,
  getSelectedGroup,
  getSelectedGroupValue,
  getSelectedGroupSiblings,
  getSortedGroupContributors,
  getIncludeNotHeld,
} from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"
import { getSelectedAccount } from "modules/account-root/selectors"
import { convertPositionSetType } from "helpers/compositeSecurities"
import { getPage } from "helpers/compositeSecuritiesDetails"
import { generateClassificationQueryForContributors } from "helpers/classification"
import { getQueryGroupBy } from "helpers/rollups"
import { Creators } from "./model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { VIEW_TYPES } from "helpers/base"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: getSelectedAccount(state),
    includeNotHeld: getIncludeNotHeld(state, props),
  }),
  required: ["account", "positionSetType", "positionSetId", "modelId"],
  data: [{
    resource: "compositeExposureContributors",
    as: null,
    url: ({ positionSetType, positionSetId, modelId, selectedDate, viewType, expand, base, baseSignature, contentSetId, segment, classifications, includeNotHeld }) => {
      if (selectedDate) {
        return {
          id: `compositeExposureContributors-${modelId}${positionSetType}${positionSetId}${selectedDate}${viewType}${expand}${baseSignature}${contentSetId}${JSON.stringify(segment)}${includeNotHeld}`,
          query: `query ($modelId: String!, $positionSetType: PositionSetType!, $positionSetId: String!, $date: Date!, $expand: Boolean, $base: [PositionSetInput], $contentSetId: String, $segment: Segment, $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: $positionSetType,
                id: $positionSetId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, base: $base, interval: END_DATE) {
                exposureContributors (contentSetId: $contentSetId, includeNotHeld: $includeNotHeld) {
                  date
                  contributors {
                    id
                    assetClass
                    assetSubclass
                    notHeld
                    country
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    description
                    percentEquity
                    bloombergTicker
                    modelProviderId
                    factors {
                      id
                      category
                      exposure: netContribution
                      currencyExposure
                    }
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            positionSetType: convertPositionSetType(positionSetType),
            positionSetId,
            date: selectedDate,
            contentSetId,
            base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
            segment,
            includeNotHeld,
            expand,
          },
        }
      }

      return null
    },
  }, {
    resource: "compositeGroupedExposureContributors",
    as: null,
    url: ({ modelId, positionSetType, positionSetId, selectedDate, viewType, expand, base, baseSignature, currentGrouping, contentSetId, segment, classifications, includeNotHeld }) => {
      if (!currentGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(currentGrouping, viewType === VIEW_TYPES.ACTIVE)

      return {
        id: `compositeGroupedExposureContributors-${modelId}${positionSetType}${positionSetId}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(queryGroupBy)}${contentSetId}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $positionSetType: PositionSetType!, $positionSetId: String!, $date: Date!, $expand: Boolean, $base: [PositionSetInput], $groupBy: ContributorGroupType!, $contentSetId: String, $segment: Segment, $groupById: String, $tier: String, $includeNotHeld: Boolean) {
                model (id: $modelId) {
                  simulation (positionSet: {
                    type: $positionSetType,
                    id: $positionSetId,
                    segment: $segment,
                    expand: $expand
                  }, from: $date, to: $date, base: $base, interval: END_DATE) {
                    groupedExposureContributors (contentSetId: $contentSetId, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier, includeNotHeld: $includeNotHeld) {
                      date
                      groupedContributors {
                        name
                        id
                        totalPercentEquity
                        factors {
                          id
                          category
                          exposure: netContribution
                          currencyExposure
                        }
                        contributors {
                          id
                          assetClass
                          notHeld
                          country
                          ${generateClassificationQueryForContributors("GICS", classifications)}
                          currency
                          description
                          percentEquity
                          bloombergTicker
                          modelProviderId
                          factors {
                            id
                            category
                            exposure: netContribution
                            currencyExposure
                          }
                        }
                      }
                    }
                  }
                }
              }`,
        variables: {
          modelId,
          positionSetType: convertPositionSetType(positionSetType),
          positionSetId,
          date: selectedDate,
          contentSetId,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          ...queryGroupBy,
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    contributors: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    excelData: getExcelData(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    page: getPage(state, props),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
